/**
 * Member loading utilities
 * Shared helpers for loading and formatting member data
 */

import type { FieldOption } from '@/lib/fields/types'
import { MemberApi } from '@/lib/api/members.api'

/**
 * Member data for UI display (bulk operations, etc.)
 */
export interface MemberDisplayData {
  id: string
  name: string
  email: string
  avatarUrl?: string
}

/**
 * Format member name with fallbacks
 */
export function formatMemberName(member: {
  displayName?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}): string {
  // A display name that is just the email address is no name at all.
  if (member.displayName && !member.displayName.includes('@'))
    return member.displayName

  const fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim()
  if (fullName) return fullName

  return member.email || 'Unknown'
}

/**
 * Load members for field options (dropdowns, multi-selects)
 * Returns data formatted for FieldOption type
 */
export async function loadMembersForFieldOptions(): Promise<FieldOption[]> {
  const memberApi = MemberApi.getInstance()
  const response = await memberApi.getMembersForDisplay({
    page: 1,
    limit: 100,
    status: 'ACTIVE',
  })

  return response.data.map(member => ({
    label: formatMemberName(member.user),
    value: member.userId,
  }))
}

/**
 * Load members for display (bulk actions, assignee lists)
 * Returns full member data including avatar
 */
export async function loadMembersForDisplay(): Promise<MemberDisplayData[]> {
  const memberApi = MemberApi.getInstance()
  const response = await memberApi.getMembersForDisplay({
    page: 1,
    limit: 100,
    status: 'ACTIVE',
  })

  return response.data.map(member => ({
    id: member.userId,
    name: formatMemberName(member.user),
    email: member.user.email || '',
    avatarUrl: member.user.image || undefined,
  }))
}
